import React, { FC } from 'react';

import { HRule } from 'Shared/components';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { isPayInFullPlan } from 'QuoteApp/utils/paymentPlan';

import { FeeBreakdownSection, SummarySection } from './SidebarSections';
import { buildDownPaymentFeeLines, buildInstallmentFeeLines } from './utils';

type FlexiblePaymentSidebarProps = {
  paymentPlan: PaymentPlanType;
  excludedDrivers: number;
  includedDrivers: number;
};

export const FlexiblePaymentSidebar: FC<FlexiblePaymentSidebarProps> = ({
  paymentPlan,
  excludedDrivers,
  includedDrivers,
}) => {
  const {
    details,
    api,
    config,
    rate,
    rateLoading,
    detailsLoading,
    fetchRate,
    refreshDetails,
  } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const feeStrategy = details.paymentPreferences.variableFeeStrategySelected
    ? 'variable'
    : 'predictable';

  const hasFees = paymentPlan.paymentSchedule.some(schedule =>
    schedule.items?.some(item => item.type === 'FEE')
  );

  const hasSupportForPredictableFee = !!config?.accessEnabled;

  const buildUpdatePayloadForStrategy = (strategyName: string) => {
    return {
      paymentPreferences: {
        variableFeeStrategySelected: strategyName === 'variable',
      },
    };
  };

  return (
    <>
      {rate && hasFees && (
        <>
          <FeeBreakdownSection
            rate={rate}
            feeStrategy={feeStrategy}
            downPaymentFeeLines={buildDownPaymentFeeLines(paymentPlan)}
            installmentFeeLines={buildInstallmentFeeLines(paymentPlan)}
            isPayInFullPlan={isPayInFullPlan(paymentPlan)}
            hasSupportForPredictableFee={hasSupportForPredictableFee}
            isLoading={rateLoading || detailsLoading}
            onStrategySelect={strategyName => {
              api
                .saveAppData({
                  data: buildUpdatePayloadForStrategy(strategyName),
                })
                .then(async () => {
                  await fetchRate();
                  // Refresh details to reflect the latest data generated after rate
                  await refreshDetails();
                  await getSignaturePacket();
                });
            }}
          />
          <HRule size="sm" />
        </>
      )}

      <SummarySection
        productName="Auto"
        effectiveDate={details.effectiveDate.value}
        excludedDrivers={excludedDrivers}
        numberOfDrivers={includedDrivers}
        numberOfVehicles={Object.keys(details.vehicles).length}
      />
    </>
  );
};
